import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { CheckCircle, XCircle, Mail, Loader2 } from 'lucide-react';

export const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setStatus('error');
      setMessage('No verification token provided');
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await authAPI.verifyEmail(token);
      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully!');
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. The link may have expired or already been used.');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="verify-email-page">
      <div className="max-w-md w-full bg-card rounded-2xl p-8 text-center border border-border">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-foreground mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Verifying Your Email
            </h1>
            <p className="text-muted-foreground">Please wait while we verify your email address...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Email Verified!
            </h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <p className="text-sm text-muted-foreground mb-6">Redirecting to dashboard...</p>
            <Link
              to="/dashboard"
              className="inline-flex items-center px-6 py-3 rounded-full bg-primary text-white font-semibold hover:bg-primary/90"
            >
              Go to Dashboard
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Verification Failed
            </h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <div className="space-y-3">
              <Link
                to="/auth"
                className="inline-flex items-center px-6 py-3 rounded-full bg-primary text-white font-semibold hover:bg-primary/90"
              >
                <Mail className="w-5 h-5 mr-2" />
                Sign In & Resend
              </Link>
              <p className="text-sm text-muted-foreground">
                Need help? <Link to="/emergency" className="text-primary hover:underline">Contact Support</Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
